import React from 'react';
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import i18next from 'i18next';

import t from '../../../i18n';

import { Switch } from '@smartplatform/ui';

import './styles.scss';

const mapToSelect = row => ({ value: row.id, label: row.name });

const SYS_MODELS = ['Role', 'ACL', 'RoleMapping', 'User'];
const HIDDEN_MODELS = ['Container', 'Attachment', 'AccessToken', 'Comment', 'Setting', 'Model'];

@inject('store') @observer
class ACLGrid extends React.Component {
	
	@observable gridData = [];
	@observable acls = [];
	@observable allWrite = true;
	@observable allRead = true;
	@observable isLoading = false;
	
	constructor (props) {
		super(props);
		this.store = props.store;
		this.props = props;
		this.onChange = this.props.onChange;
		
		this.models = Object.keys(this.store._models).sort((a, b) => (
			SYS_MODELS.includes(a) ? 1 : SYS_MODELS.includes(b) ? -1 : (a < b) ? -1 : (a > b) ? 1 : 0
		)).filter(a => !HIDDEN_MODELS.includes(a)).map(modelName => {
			let modelTitle = i18next.exists(`${modelName}.title`) ? t(`${modelName}.title`) : '';
			let modelDescription = i18next.exists(`${modelName}.roleDescription`) ? t(`${modelName}.roleDescription`) : '';
			
			let info = {
				value: modelName,
				label: modelTitle ? modelTitle + ' (' + modelName + ')' : modelName,
				description: modelDescription,
				sysModel: SYS_MODELS.includes(modelName)
			};
			
			return info;
		});
		
		this.init(props);
	}
	
	@action init = async (props) => {
		this.isLoading = true;
		
		this.acls = await this.store.ACL.find({
			where: {
				and: [
					{ principalId: props.principalId },
					{ principalType: props.principalType }
				]
			}
		});
		
		this.models.forEach(model => {
			let read = { aclId: null, value: false };
			let write = { aclId: null, value: false };
			let execute = { aclId: null, value: false };
			
			let resultRead = this.acls.find(acl => {
				return acl.model === model.value && acl.accessType == 'READ' && acl.permission == 'ALLOW';
			});
			
			let resultWrite = this.acls.find(acl => {
				return acl.model === model.value && acl.accessType == 'WRITE' && acl.permission == 'ALLOW';
			});
			
			let resultExecute = this.acls.find(acl => {
				return acl.model === model.value && acl.accessType == 'EXECUTE' && acl.permission == 'ALLOW';
			});
			
			if (resultRead) read = { aclId: resultRead.id, value: true };
			if (resultWrite) write = { aclId: resultWrite.id, value: true };
			if (resultExecute) execute = { aclId: resultExecute.id, value: true };
			
			if (!read.value && this.allRead) this.allRead = false;
			if (!write.value && this.allWrite) this.allWrite = false;
			
			this.gridData.push({
				model: { value: model.value, label: model.label, description: model.description, sysModel: model.sysModel },
				read: read,
				write: write,
				execute: execute,
				changed: false,
			});
		});
		
		this.isLoading = false;
	};
	
	changeAccess (value, index, type) {
		this.gridData[index].changed = true;
		if (type === 'read') {
			this.gridData[index].read.value = value;
			if (!value) this.allRead = false;
		} else if (type === 'write') {
			this.gridData[index].write.value = value;
			if (!value) this.allWrite = false;
		} else if (type === 'execute') {
			this.gridData[index].execute.value = value;
		}
		
		this.onChange(this.convertGridData(this.gridData));
	}
	
	changeAllAccess (index) {
		if (this.gridData[index].read.value && this.gridData[index].write.value && this.gridData[index].execute.value) {
			this.gridData[index].read.value = false;
			this.gridData[index].write.value = false;
			this.gridData[index].execute.value = false;
		} else {
			this.gridData[index].read.value = true;
			this.gridData[index].write.value = true;
			this.gridData[index].execute.value = true;
		}
		
		this.onChange(this.convertGridData(this.gridData));
	}
	
	convertGridData (data) {
		let acls = [];
		
		data.forEach(dataItem => {
			if (!dataItem.changed) return;
			
			acls.push({
				id: dataItem.read.aclId,
				model: dataItem.model.value,
				accessType: 'READ',
				permission: (dataItem.read.value) ? 'ALLOW' : 'DENY',
				principalType: this.props.principalType,
				principalId: this.props.principalId
			});
			
			acls.push({
				id: dataItem.write.aclId,
				model: dataItem.model.value,
				accessType: 'WRITE',
				permission: (dataItem.write.value) ? 'ALLOW' : 'DENY',
				principalType: this.props.principalType,
				principalId: this.props.principalId
			});
			
			acls.push({
				id: dataItem.execute.aclId,
				model: dataItem.model.value,
				accessType: 'EXECUTE',
				permission: (dataItem.execute.value) ? 'ALLOW' : 'DENY',
				principalType: this.props.principalType,
				principalId: this.props.principalId
			});
		});
		
		return acls;
	}
	
	changeAllRead = value => {
		this.allRead = !this.allRead;
		this.gridData.forEach(data => {
			data.read.value = this.allRead;
			data.changed = true;
		});
		this.onChange(this.convertGridData(this.gridData));
	};
	
	@action changeAllWrite = value => {
		this.allWrite = !this.allWrite;
		this.gridData.forEach(data => {
			data.write.value = this.allWrite;
			data.changed = true;
		});
		
		this.onChange(this.convertGridData(this.gridData));
	};
	
	render () {
		const rows = this.gridData.map((gridItem, i) => {
			return <tr key={i} className={`table-row ${gridItem.model.sysModel ? 'sys-role' : ''}`}>
				<td className="table-cell table-cell-auto">{gridItem.model.label}</td>
				<td className="table-cell table-cell-switch">
					<Switch value={gridItem.read.value} onChange={(e) => this.changeAccess(!gridItem.read.value, i, 'read', e)} />
				</td>
				<td className="table-cell table-cell-switch">
					<Switch value={gridItem.write.value} onChange={(e) => this.changeAccess(!gridItem.write.value, i, 'write', e)} />
				</td>
			</tr>
		});
		
		return <div className="acl-grid">
			<label className="acl-grid-title">{t('acl.list')}</label>
			
			<table className="acl-table">
				<thead className="table-head">
				<tr className="table-row">
					<th className="table-cell">{t('acl.model.title')}</th>
					<th className="table-cell table-cell-switch">{t('acl.read')}</th>
					<th className="table-cell table-cell-switch">{t('acl.write')}</th>
				</tr>
				</thead>
				
				{!this.isLoading && (
					<tbody className="table-body">
					<tr key="all" className="table-row row-all">
						<td className="table-cell table-cell-auto">{t('all')}</td>
						<td className="table-cell table-cell-switch">
							<Switch active={this.allRead} onChange={this.changeAllRead} />
						</td>
						<td className="table-cell table-cell-switch">
							<Switch active={this.allWrite} onChange={this.changeAllWrite} />
						</td>
					</tr>
					
					{rows}
					</tbody>
				)}
			</table>
		</div>
	}
}

ACLGrid.propTypes = {
	onChange: PropTypes.func,
	principalId: PropTypes.string,
	principalType: PropTypes.string
};

export default ACLGrid;
